import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ApiError, clearAdminKey, getAdminKey, setAdminKey } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { useToast } from '../components/Toast';
import { useState } from 'react';

export function AdminFeedbackPage() {
  const [hasKey, setHasKey] = useState(() => Boolean(getAdminKey()));
  if (!hasKey) return <AdminFeedbackKeyPrompt onSet={() => setHasKey(true)} />;
  return <AdminFeedbackList onSignOut={() => setHasKey(false)} />;
}

function AdminFeedbackKeyPrompt({ onSet }: { onSet: () => void }) {
  const [key, setKey] = useState('');
  return (
    <div className="container-page max-w-md py-24">
      <h1 className="text-2xl font-semibold tracking-tight">Admin sign in</h1>
      <p className="mt-2 text-sm text-ink-muted">Enter your password to continue.</p>
      <form
        className="mt-6 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          setAdminKey(key.trim());
          onSet();
        }}
      >
        <input
          type="password"
          autoFocus
          value={key}
          onChange={(e) => setKey(e.target.value)}
          className="input"
          placeholder="Password"
        />
        <button type="submit" className="btn-primary w-full" disabled={key.trim().length === 0}>
          Continue
        </button>
      </form>
    </div>
  );
}

function AdminFeedbackList({ onSignOut }: { onSignOut: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'feedback'],
    queryFn: () => api.admin.listFeedbackReports(),
    retry: false,
  });
  const updateStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'open' | 'closed' }) =>
      api.admin.updateFeedbackReportStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'feedback'] });
    },
    onError: (err) => toast.error('Update failed', err instanceof ApiError ? err.message : String(err)),
  });

  const reports = query.data?.reports ?? [];

  if (query.isError) {
    const msg = query.error instanceof ApiError && query.error.status === 401
      ? 'Admin key was rejected. Sign out and try again.'
      : 'Failed to load issue reports.';
    return (
      <div className="container-page py-16">
        <p className="text-sm text-red-600">{msg}</p>
        <button
          className="btn-secondary mt-4"
          onClick={() => {
            clearAdminKey();
            onSignOut();
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="container-page py-10">
      <div className="flex items-end justify-between gap-4">
        <div>
          <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Admin</Link>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight">Issue reports</h1>
        </div>
        <span className="chip-pill">{reports.filter((r) => r.status === 'open').length} open</span>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-surface shadow-card">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-line bg-canvas/70 text-xs uppercase tracking-wide text-ink-subtle">
            <tr>
              <th className="px-5 py-3 font-medium">Report</th>
              <th className="px-5 py-3 font-medium">Page</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Created</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-ink-muted">
                  No reports yet.
                </td>
              </tr>
            ) : (
              reports.map((report) => (
                <tr key={report.id} className="border-b border-line last:border-0">
                  <td className="max-w-lg px-5 py-4 align-top">
                    <p className="whitespace-pre-wrap text-ink">{report.message}</p>
                    {report.contactEmail ? (
                      <p className="mt-2 text-xs text-ink-subtle">{report.contactEmail}</p>
                    ) : null}
                    {report.viewport || report.userAgent ? (
                      <p className="mt-1 truncate text-[11px] text-ink-subtle">
                        {[report.viewport, report.userAgent].filter(Boolean).join(' · ')}
                      </p>
                    ) : null}
                  </td>
                  <td className="max-w-xs px-5 py-4 align-top text-xs text-ink-muted">
                    {report.pageUrl ? (
                      <a href={report.pageUrl} target="_blank" rel="noreferrer" className="break-all hover:text-accent">
                        {report.pageUrl}
                      </a>
                    ) : (
                      <span>Unknown</span>
                    )}
                  </td>
                  <td className="px-5 py-4 align-top">
                    <button
                      type="button"
                      className={report.status === 'open' ? 'chip bg-accent-soft text-accent' : 'chip bg-canvas text-ink-muted'}
                      disabled={updateStatus.isPending}
                      onClick={() =>
                        updateStatus.mutate({
                          id: report.id,
                          status: report.status === 'open' ? 'closed' : 'open',
                        })
                      }
                    >
                      {report.status}
                    </button>
                  </td>
                  <td className="px-5 py-4 align-top text-xs text-ink-muted">
                    {formatDateTime(report.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
